import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Filter } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import ProductCard from "../components/ProductCard";
import { getCategories, getProductFacets, getProducts } from "../lib/api";
import Seo from "../components/Seo";
import { getSiteUrl } from "../lib/seo";

const PRODUCT_PAGE_SIZE = 20;

const DEFAULT_CATEGORIES = [
  { value: "todos", label: "Todos" },
  { value: "hombre", label: "Hombre" },
  { value: "mujer", label: "Mujer" },
  { value: "accesorios", label: "Accesorios" },
];

const normalizeKey = (value) =>
  value
    .toString()
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const normalizeFilterList = (items) => {
  const normalized = items.map(normalizeKey).filter(Boolean);
  return Array.from(new Set(normalized)).sort((a, b) => a.localeCompare(b));
};

const formatBrandLabel = (value) =>
  value.toString().trim().replace(/\s+/g, " ").toUpperCase();

const getDefaultCategoryImage = (slugOrTemplate) => {
  if (slugOrTemplate === "hombre") return "/hombre.jpg";
  if (slugOrTemplate === "mujer") return "/mujer.jpg";
  if (slugOrTemplate === "accesorios") return "/accesorios.jpg";
  return "/accesorios.jpg";
};

const formatColorLabel = (value) => {
  const cleaned = value.toString().trim().replace(/\s+/g, " ").toLowerCase();
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

const buildFacetList = (items, formatLabel) => {
  const map = new Map();
  items.forEach((raw) => {
    if (!raw) return;
    const key = normalizeKey(raw);
    if (!key) return;
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
      return;
    }
    map.set(key, {
      key,
      label: formatLabel(raw),
      count: 1,
    });
  });
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
};

const parseList = (value) => (value ? normalizeFilterList(value.split(",")) : []);

const parseNumberValue = (value) => {
  if (!value) return "";
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? "" : value;
};

const areListsEqual = (left, right) =>
  left.length === right.length && left.every((item, index) => item === right[index]);

const Catalogo = () => {
  const { categoria } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get("search") || "";

  const [productos, setProductos] = useState([]);
  const [categories, setCategories] = useState([]);
  const [facetOptions, setFacetOptions] = useState({ brands: [], colors: [] });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreProducts, setHasMoreProducts] = useState(true);
  const [nextOffset, setNextOffset] = useState(0);
  const [loadError, setLoadError] = useState("");
  const [filtros, setFiltros] = useState(() => ({
    marcas: parseList(searchParams.get("marcas")),
    colores: parseList(searchParams.get("colores")),
    precioMin: parseNumberValue(searchParams.get("min")),
    precioMax: parseNumberValue(searchParams.get("max")),
  }));
  const [orden, setOrden] = useState(searchParams.get("orden") || "recientes");
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const loadMoreRef = useRef(null);
  const loadingPageRef = useRef(false);
  const queryVersionRef = useRef(0);
  const activeProductRequestRef = useRef(0);

  useEffect(() => {
    let isMounted = true;
    getCategories()
      .then((data) => {
        if (!isMounted) return;
        setCategories(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!isMounted) return;
        setCategories([]);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    const params = {};
    if (categoria && categoria !== "todos") {
      params.category = categoria;
    }
    if (search.trim()) {
      params.search = search.trim();
    }

    getProductFacets(params)
      .then((data) => {
        if (!isMounted) return;
        setFacetOptions({
          brands: Array.isArray(data?.brands) ? data.brands : [],
          colors: Array.isArray(data?.colors) ? data.colors : [],
        });
      })
      .catch(() => {
        if (!isMounted) return;
        setFacetOptions({ brands: [], colors: [] });
      });

    return () => {
      isMounted = false;
    };
  }, [categoria, search]);

  useEffect(() => {
    const nextFiltros = {
      marcas: parseList(searchParams.get("marcas")),
      colores: parseList(searchParams.get("colores")),
      precioMin: parseNumberValue(searchParams.get("min")),
      precioMax: parseNumberValue(searchParams.get("max")),
    };
    setFiltros((prev) => {
      if (
        areListsEqual(prev.marcas, nextFiltros.marcas) &&
        areListsEqual(prev.colores, nextFiltros.colores) &&
        prev.precioMin === nextFiltros.precioMin &&
        prev.precioMax === nextFiltros.precioMax
      ) {
        return prev;
      }
      return nextFiltros;
    });

    const nextOrden = searchParams.get("orden") || "recientes";
    setOrden((prev) => (prev === nextOrden ? prev : nextOrden));
  }, [searchParams]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (search.trim()) {
      params.set("search", search.trim());
    }
    if (filtros.marcas.length) {
      params.set("marcas", filtros.marcas.join(","));
    }
    if (filtros.colores.length) {
      params.set("colores", filtros.colores.join(","));
    }
    if (filtros.precioMin) {
      params.set("min", filtros.precioMin);
    }
    if (filtros.precioMax) {
      params.set("max", filtros.precioMax);
    }
    params.set("orden", orden);

    const next = params.toString();
    if (next !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [filtros, orden, search, searchParams, setSearchParams]);

  const productQuery = useMemo(() => {
    const params = {};
    if (categoria && categoria !== "todos") {
      params.category = categoria;
    }
    if (search.trim()) {
      params.search = search.trim();
    }
    if (filtros.marcas.length) {
      params.brands = filtros.marcas.join(",");
    }
    if (filtros.colores.length) {
      params.colors = filtros.colores.join(",");
    }
    if (filtros.precioMin) {
      params.min_price = filtros.precioMin;
    }
    if (filtros.precioMax) {
      params.max_price = filtros.precioMax;
    }
    params.sort = orden;
    return params;
  }, [
    categoria,
    search,
    filtros.marcas,
    filtros.colores,
    filtros.precioMin,
    filtros.precioMax,
    orden,
  ]);

  const loadProductsPage = useCallback(
    async (offset, replace = false, version = queryVersionRef.current) => {
      if (loadingPageRef.current) return;

      loadingPageRef.current = true;
      const requestId = activeProductRequestRef.current + 1;
      activeProductRequestRef.current = requestId;
      if (replace) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      setLoadError("");

      try {
        const data = await getProducts({
          ...productQuery,
          limit: PRODUCT_PAGE_SIZE,
          offset,
        });
        if (
          version !== queryVersionRef.current ||
          requestId !== activeProductRequestRef.current
        ) {
          return;
        }

        const pageItems = Array.isArray(data) ? data : [];
        setProductos((prev) => {
          if (replace) return pageItems;
          const currentIds = new Set(prev.map((producto) => producto.id));
          const nuevos = pageItems.filter((producto) => !currentIds.has(producto.id));
          return [...prev, ...nuevos];
        });
        setNextOffset(offset + pageItems.length);
        setHasMoreProducts(pageItems.length === PRODUCT_PAGE_SIZE);
      } catch (_) {
        if (
          version !== queryVersionRef.current ||
          requestId !== activeProductRequestRef.current
        ) {
          return;
        }
        if (replace) {
          setProductos([]);
        }
        setHasMoreProducts(false);
        setLoadError("No se pudieron cargar mas productos. Intenta recargar la pagina.");
      } finally {
        if (
          version === queryVersionRef.current &&
          requestId === activeProductRequestRef.current
        ) {
          setLoading(false);
          setLoadingMore(false);
          loadingPageRef.current = false;
        }
      }
    },
    [productQuery],
  );

  useEffect(() => {
    queryVersionRef.current += 1;
    activeProductRequestRef.current += 1;
    loadingPageRef.current = false;
    const version = queryVersionRef.current;
    setProductos([]);
    setNextOffset(0);
    setHasMoreProducts(true);
    setLoadError("");
    loadProductsPage(0, true, version);
  }, [loadProductsPage]);

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target || loading || loadingMore || !hasMoreProducts) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadProductsPage(nextOffset, false, queryVersionRef.current);
        }
      },
      { rootMargin: "600px 0px" },
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [hasMoreProducts, loadProductsPage, loading, loadingMore, nextOffset]);

  const marcasDisponibles = useMemo(() => {
    if (facetOptions.brands.length) {
      return facetOptions.brands;
    }
    const marcas = productos.map((producto) => producto.brand).filter(Boolean);
    return buildFacetList(marcas, formatBrandLabel);
  }, [facetOptions.brands, productos]);

  const coloresDisponibles = useMemo(() => {
    if (facetOptions.colors.length) {
      return facetOptions.colors;
    }
    const colores = [];
    productos.forEach((producto) => {
      if (!producto.color) return;
      producto.color.split(",").forEach((color) => {
        const trimmed = color.trim();
        if (trimmed) {
          colores.push(trimmed);
        }
      });
    });
    return buildFacetList(colores, formatColorLabel);
  }, [facetOptions.colors, productos]);

  const toggleMarca = (marca) => {
    setFiltros((prev) => {
      const existe = prev.marcas.includes(marca);
      const next = existe
        ? prev.marcas.filter((item) => item !== marca)
        : [...prev.marcas, marca];
      return { ...prev, marcas: normalizeFilterList(next) };
    });
  };

  const toggleColor = (color) => {
    setFiltros((prev) => {
      const existe = prev.colores.includes(color);
      const next = existe
        ? prev.colores.filter((item) => item !== color)
        : [...prev.colores, color];
      return { ...prev, colores: normalizeFilterList(next) };
    });
  };

  const limpiarFiltros = () => {
    setFiltros({ marcas: [], colores: [], precioMin: "", precioMax: "" });
  };

  const nombreCategoria =
    categoria && categoria !== "todos"
      ? categories.find((item) => item.slug === categoria)?.name ||
        `${categoria.charAt(0).toUpperCase()}${categoria.slice(1)}`
      : "Todos";
  const currentCategory =
    categoria && categoria !== "todos"
      ? categories.find((item) => item.slug === categoria) || null
      : null;
  const categoryOptions =
    categories.length > 0
      ? [
          { value: "todos", label: "Todos" },
          ...categories.map((item) => ({ value: item.slug, label: item.name })),
        ]
      : DEFAULT_CATEGORIES;
  const siteUrl = getSiteUrl();
  const canonicalUrl = siteUrl
    ? `${siteUrl}/catalogo/${categoria || "todos"}`
    : undefined;
  const seoTitle =
    categoria && categoria !== "todos"
      ? `Tenis para ${nombreCategoria}`
      : "Catalogo de tenis";
  const seoDescription =
    categoria && categoria !== "todos"
      ? currentCategory?.description?.trim() ||
        `Compra tenis para ${nombreCategoria.toLowerCase()} en Colombia con envios rapidos y pago a cuotas.`
      : "Explora el catalogo de tenis, zapatillas y accesorios en Colombia.";
  const hasFilters =
    search.trim() ||
    filtros.marcas.length > 0 ||
    filtros.colores.length > 0 ||
    filtros.precioMin ||
    filtros.precioMax;
  const seoRobots = hasFilters ? "noindex, follow" : "index, follow";
  const seoJsonLd = siteUrl
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Inicio",
            item: `${siteUrl}/`,
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Catalogo",
            item: `${siteUrl}/catalogo/todos`,
          },
          ...(categoria && categoria !== "todos"
            ? [
                {
                  "@type": "ListItem",
                  position: 3,
                  name: nombreCategoria,
                  item: `${siteUrl}/catalogo/${categoria}`,
                },
              ]
            : []),
        ],
      }
    : undefined;

  const handleCategoriaChange = (value) => {
    const query = searchParams.toString();
    const destino = `/catalogo/${value}`;
    navigate(query ? `${destino}?${query}` : destino);
  };

  const FiltrosContent = () => (
    <div className="space-y-6">
      <div>
        <h3 className="font-semibold mb-3">Categoria</h3>
        <Select
          value={categoria || "todos"}
          onValueChange={(value) => handleCategoriaChange(value)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {categoryOptions.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div>
        <h3 className="font-semibold mb-3">Marca</h3>
        {marcasDisponibles.length === 0 ? (
          <p className="text-sm text-gray-500">Sin marcas disponibles</p>
        ) : (
          <div className="space-y-2">
            {marcasDisponibles.map((marca) => (
              <div key={marca.key} className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`marca-${marca.key.replace(/\s+/g, "-")}`}
                    checked={filtros.marcas.includes(marca.key)}
                    onCheckedChange={() => toggleMarca(marca.key)}
                  />
                  <Label
                    htmlFor={`marca-${marca.key.replace(/\s+/g, "-")}`}
                    className="text-sm cursor-pointer"
                  >
                    {marca.label}
                  </Label>
                </div>
                <span className="text-xs text-gray-500">({marca.count})</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-semibold mb-3">Color</h3>
        {coloresDisponibles.length === 0 ? (
          <p className="text-sm text-gray-500">Sin colores disponibles</p>
        ) : (
          <div className="space-y-2">
            {coloresDisponibles.map((color) => (
              <div key={color.key} className="flex items-center justify-between gap-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`color-${color.key.replace(/\s+/g, "-")}`}
                    checked={filtros.colores.includes(color.key)}
                    onCheckedChange={() => toggleColor(color.key)}
                  />
                  <Label
                    htmlFor={`color-${color.key.replace(/\s+/g, "-")}`}
                    className="text-sm cursor-pointer"
                  >
                    {color.label}
                  </Label>
                </div>
                <span className="text-xs text-gray-500">({color.count})</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <h3 className="font-semibold mb-3">Precio</h3>
        <div className="space-y-2">
          <Input
            type="number"
            placeholder="Minimo"
            value={filtros.precioMin}
            onChange={(event) =>
              setFiltros((prev) => ({ ...prev, precioMin: event.target.value }))
            }
          />
          <Input
            type="number"
            placeholder="Maximo"
            value={filtros.precioMax}
            onChange={(event) =>
              setFiltros((prev) => ({ ...prev, precioMax: event.target.value }))
            }
          />
        </div>
      </div>

      <Button onClick={limpiarFiltros} variant="outline" className="w-full">
        Limpiar filtros
      </Button>
    </div>
  );

  return (
    <div className="container mx-auto px-4 py-8">
      <Seo
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalUrl}
        robots={seoRobots}
        jsonLd={seoJsonLd}
      />
      <div className="mb-6">
        <p className="text-sm text-gray-600">
          Inicio / {nombreCategoria}
          {search && ` / Busqueda: "${search}"`}
        </p>
      </div>

      {currentCategory && (
        <section className="mb-8 overflow-hidden rounded-2xl border border-black/10 bg-white shadow-sm">
          <div className="grid gap-0 md:grid-cols-[1.2fr_0.8fr]">
            <div className="p-6 md:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500">
                Categoria
              </p>
              <h1 className="mt-3 text-3xl font-bold text-gray-900">{currentCategory.name}</h1>
              <p className="mt-4 max-w-2xl text-sm leading-6 text-gray-600">
                {currentCategory.description?.trim() ||
                  `Explora la seleccion de ${currentCategory.name.toLowerCase()} disponible en la tienda.`}
              </p>
            </div>
            <div className="min-h-56 bg-gray-100">
              <img
                src={
                  currentCategory.image_url ||
                  getDefaultCategoryImage(currentCategory.template_slug || currentCategory.slug)
                }
                alt={currentCategory.name}
                className="h-full w-full object-cover"
              />
            </div>
          </div>
        </section>
      )}

      <div className="flex flex-col md:flex-row gap-8">
        <aside className="hidden md:block w-64 flex-shrink-0">
          <div className="sticky top-24">
            <h2 className="text-xl font-bold mb-4">Filtros</h2>
            <FiltrosContent />
          </div>
        </aside>

        <div className="flex-1">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <p className="text-sm text-gray-600">
              {loading && productos.length === 0
                ? "Cargando productos..."
                : `Mostrando ${productos.length} productos cargados`}
            </p>

            <div className="flex items-center gap-4">
              <Sheet open={filtrosAbiertos} onOpenChange={setFiltrosAbiertos}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm" className="md:hidden">
                    <Filter className="h-4 w-4 mr-2" />
                    Filtros
                  </Button>
                </SheetTrigger>
                <SheetContent side="left">
                  <SheetHeader>
                    <SheetTitle>Filtros</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6">
                    <FiltrosContent />
                  </div>
                </SheetContent>
              </Sheet>

              <Select value={orden} onValueChange={setOrden}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recientes">Recientes</SelectItem>
                  <SelectItem value="nombre">Nombre</SelectItem>
                  <SelectItem value="precio-asc">Precio: Menor a Mayor</SelectItem>
                  <SelectItem value="precio-desc">Precio: Mayor a Menor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {productos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
              {productos.map((producto) => (
                <ProductCard key={producto.id} producto={producto} />
              ))}
            </div>
          ) : null}
          {!loading && loadError && productos.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-red-600">{loadError}</p>
            </div>
          ) : null}
          {!loading && !loadError && productos.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-gray-500 text-lg">No se encontraron productos</p>
              <Button onClick={limpiarFiltros} className="mt-4">
                Limpiar filtros
              </Button>
            </div>
          ) : null}
          {productos.length > 0 ? (
            <div ref={loadMoreRef} className="py-10 text-center">
              {loadingMore && (
                <p className="text-sm text-gray-500">Cargando mas productos...</p>
              )}
              {!loadingMore && !hasMoreProducts && (
                <p className="text-sm text-gray-400">Llegaste al final del catalogo.</p>
              )}
              {loadError && <p className="text-sm text-red-600">{loadError}</p>}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Catalogo;
